import { Injectable, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { driver, DriveStep } from 'driver.js';
import { SpinnerService } from './spinner.service';
import { TranslateService } from '@ngx-translate/core';
import { CustomDriveStep } from '../models/customDriverSteps';

@Injectable({ providedIn: 'root' })
export class OnBoardingService {
  readonly isTourActive = signal(false);
  readonly tourCompletedVersion = signal(0);
  private currentKey: string | null = null;
  private translateService = inject(TranslateService);
  private completedTours = new Set<string>();
  private storageKeyPrefix = 'onboarding_';
  private pendingTour: { steps: CustomDriveStep[]; key: string } | null = null;
  private previousIndex = -1;
  private actionAbortController: AbortController | null = null;
  private readonly escapeKeyHandler = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || !this.isTourActive()) return;
    event.preventDefault();
    event.stopPropagation();
    this.destroy();
  };

  private driverObj = driver({
    showProgress: true,
    popoverClass: 'custom-popover-class',
    animate: true,
    allowClose: false,
    stagePadding: 10,
    stageRadius: 5,
    progressText: '{{current}} of {{total}}',
    onDestroyed: () => {
      if (this.currentKey) {
        this.setCompleted(this.currentKey);
        this.currentKey = null;
      }
    },
  });

  constructor(private spinnerService: SpinnerService) {
    document.addEventListener('keydown', this.escapeKeyHandler, true);
    const router = inject(Router);
    router.events.subscribe(() => {
      this.destroy();
    });

    effect(() => {
      if (
        this.spinnerService.elementsReady() &&
        this.pendingTour &&
        !this.hasCompleted(this.pendingTour.key) &&
        !this.hasSkippedOnboarding()
      ) {
        setTimeout(() => {
          if (!this.spinnerService.loading() && this.pendingTour) {
            const { steps, key } = this.pendingTour;
            this.startTourInternal(steps, key);
            this.pendingTour = null;
          }
        }, 100);
      }
    });
  }

  private async closeMatDialog(delayMs = 350): Promise<void> {
    const backdrop = document.querySelector('.cdk-overlay-backdrop');
    if (!backdrop) return;
    backdrop.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
    await this.delay(delayMs);
    const stillOpen = document.querySelector('.cdk-overlay-backdrop');
    if (stillOpen instanceof HTMLElement) {
      stillOpen.click();
      await this.delay(delayMs);
    }
  }

  private isModalOpen(): boolean {
    return !!document.querySelector('.cdk-overlay-backdrop');
  }

  private async openModalForGroup(
    steps: CustomDriveStep[],
    targetGroup: string,
    targetElement: string,
    signal: AbortSignal,
  ): Promise<void> {
    if (!this.isModalOpen()) {
      const openerStep = steps.find(s => s.modalGroup === targetGroup && s.opensModal);
      if (openerStep) {
        const el = document.querySelector(openerStep.element);
        if (el instanceof HTMLElement) {
          el.click();
          await this.waitForCondition(() => this.isModalOpen(), 2000);
          if (signal.aborted) return;
        }
      }
    }
    await this.waitFor(targetElement, 3000);
  }

  private closeOpenSelect(): void {
    const overlayPane = document.querySelector('.cdk-overlay-pane');
    if (overlayPane instanceof HTMLElement) {
      overlayPane.classList.remove('driver-additional-highlight');
      overlayPane.style.position = '';
      overlayPane.style.zIndex = '';
    }
    const backdrop = document.querySelector('.cdk-overlay-backdrop');
    if (backdrop instanceof HTMLElement) {
      backdrop.click();
    }
  }

  private collapseAccordeon(step: CustomDriveStep): void {
    if (!step.openAccordeon) return;
    const el = document.querySelector(step.element);
    if (el instanceof HTMLElement) {
      el.classList.remove('mat-expanded');
    }
  }

  private async executeStepActions(
    step: CustomDriveStep,
    goingForward: boolean,
    signal: AbortSignal,
  ): Promise<void> {
    const aborted = () => signal.aborted;

    if (step.closeBackdrop && goingForward) {
      await this.closeMatDialog();
      if (aborted()) return;
    }

    if (step.beforeHighlight) {
      await step.beforeHighlight();
      if (aborted()) return;
    }

    if (step.waitForElement) {
      await this.waitFor(step.element, step.waitForElement);
      if (aborted()) return;
    }

    const el = document.querySelector(step.element);
    if (el instanceof HTMLElement) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      await this.delay(300);
      if (aborted()) return;
    }

    const selectElement = document.querySelector(step.element);
    if (selectElement instanceof HTMLElement) {
      if (step.openAccordeon) {
        if (goingForward) {
          selectElement.classList.add('mat-expanded');
        } else {
          selectElement.classList.remove('mat-expanded');
        }
      }

      if (step.openSelect && goingForward) {
        const trigger = selectElement.querySelector('.mat-select-trigger');
        if (trigger instanceof HTMLElement) {
          trigger.click();
        } else {
          const matSelect = selectElement.querySelector('.mat-select');
          if (matSelect instanceof HTMLElement) {
            matSelect.click();
          } else {
            selectElement.click();
          }
        }
        await this.delay(500);
        if (aborted()) {
          this.closeOpenSelect();
          return;
        }
        const overlayPane = document.querySelector('.cdk-overlay-pane');
        if (overlayPane instanceof HTMLElement) {
          overlayPane.classList.add('driver-additional-highlight');
          overlayPane.style.position = 'relative';
          overlayPane.style.zIndex = '10000001';
        }
      }
    }

    if (step.highlightAdditionalElements && goingForward) {
      await this.delay(100);
      if (aborted()) return;
      step.highlightAdditionalElements.forEach(selector => {
        const element = document.querySelector(selector);
        if (element instanceof HTMLElement) {
          element.classList.add('driver-additional-highlight');
          element.style.position = 'relative';
          element.style.zIndex = '10000001';
        }
      });
    }

    if (step.triggerClick && goingForward) {
      const elToClick = document.querySelector(step.element);
      if (elToClick instanceof HTMLElement) {
        elToClick.click();
        await this.delay(300);
        if (aborted()) return;
      }
    }

    if (step.customSize) {
      this.applyCustomSize(step.element, step.customSize);
    }

    if (step.afterHighlight) {
      await step.afterHighlight();
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private waitFor(selector: string, timeout: number): Promise<void> {
    return new Promise(resolve => {
      const start = Date.now();
      const check = (): void => {
        if (document.querySelector(selector) || Date.now() - start > timeout) {
          resolve();
        } else {
          setTimeout(check, 100);
        }
      };
      check();
    });
  }

  private waitForCondition(condition: () => boolean, timeoutMs: number): Promise<void> {
    return new Promise(resolve => {
      if (condition()) {
        resolve();
        return;
      }
      const start = Date.now();
      const interval = setInterval(() => {
        if (condition() || Date.now() - start > timeoutMs) {
          clearInterval(interval);
          resolve();
        }
      }, 50);
    });
  }

  async closeBeforeNavigation(): Promise<void> {
    const hasDriverArtifacts = Boolean(
      document.querySelector(
        '#driver-blocker-overlay, .driver-overlay, .driver-popover, .driver-additional-highlight',
      ),
    );
    if (!this.isTourActive() && !hasDriverArtifacts) return;

    this.destroy();
    if (this.isTourActive()) {
      await this.waitForCondition(() => !this.isTourActive(), 1000);
    }
  }

  private cleanAdditionalHighlights(): void {
    document.querySelectorAll('.driver-additional-highlight').forEach(el => {
      if (el instanceof HTMLElement) {
        el.classList.remove('driver-additional-highlight');
        el.style.position = '';
        el.style.zIndex = '';
      }
    });
  }

  private applyCustomSize(element: string, customSize?: { width?: number; height?: number }): void {
    if (!customSize) return;
    const targetElement = document.querySelector(element) as HTMLElement;
    if (!targetElement) return;
    const originalWidth = targetElement.style.width;
    const originalHeight = targetElement.style.height;
    const originalDisplay = targetElement.style.display;
    const originalPosition = targetElement.style.position;
    if (customSize.width) targetElement.style.width = `${customSize.width}px`;
    if (customSize.height) targetElement.style.height = `${customSize.height}px`;
    targetElement.setAttribute('data-original-width', originalWidth);
    targetElement.setAttribute('data-original-height', originalHeight);
    targetElement.setAttribute('data-original-display', originalDisplay);
    targetElement.setAttribute('data-original-position', originalPosition);
  }

  private restoreOriginalSize(element: string): void {
    const targetElement = document.querySelector(element) as HTMLElement;
    if (!targetElement) return;
    const originalWidth = targetElement.getAttribute('data-original-width');
    const originalHeight = targetElement.getAttribute('data-original-height');
    if (originalWidth !== null) {
      targetElement.style.width = originalWidth;
      targetElement.removeAttribute('data-original-width');
    }
    if (originalHeight !== null) {
      targetElement.style.height = originalHeight;
      targetElement.removeAttribute('data-original-height');
    }
  }

  translateItem = (item?: string): string => (item ? this.translateService.instant(item) : '');

  private async prepareNavigation(step: CustomDriveStep | undefined): Promise<void> {
    if (step?.beforeNavigation) {
      await step.beforeNavigation();
    }
  }

  private processSteps(steps: CustomDriveStep[]): DriveStep[] {
    return steps.map((step, index) => {
      const nextStep = steps[index + 1];
      const previousStep = steps[index - 1];

      return {
        element: step.element,
        popover: {
          title: this.translateItem(step.popover.title),
          description: this.translateItem(step.popover.description),
          side: step.popover.side || 'bottom',
          align: step.popover.align || 'start',
          ...(nextStep?.beforeNavigation
            ? {
                onNextClick: async () => {
                  await this.prepareNavigation(nextStep);
                  this.driverObj.moveNext();
                },
              }
            : {}),
          ...(previousStep?.beforeNavigation
            ? {
                onPrevClick: async () => {
                  await this.prepareNavigation(previousStep);
                  this.driverObj.movePrevious();
                },
              }
            : {}),
        },
      } as DriveStep;
    });
  }

  private startTourInternal(steps: CustomDriveStep[], key: string): void {
    this.currentKey = key || null;
    const processedSteps = this.processSteps(steps);
    if (document.querySelector('.sources-panel.fixed')) {
      document.body.classList.add('onboarding-open-search-background-gallery');
    }
    this.addInteractionBlocker();
    this.isTourActive.set(true);
    this.previousIndex = -1;

    this.driverObj = driver({
      showProgress: true,
      popoverClass: 'custom-popover-class',
      animate: true,
      allowClose: false,
      stagePadding: 10,
      stageRadius: 5,
      progressText: '{{current}} of {{total}}',
      nextBtnText: this.translateItem('ONBOARDING.BUTTONS.NEXT'),
      prevBtnText: this.translateItem('ONBOARDING.BUTTONS.BACK'),
      doneBtnText: this.translateItem('ONBOARDING.BUTTONS.DONE'),
      showButtons: ['next', 'previous'],
      onDoneClick: () => {
        if (key) this.setCompleted(key);
        this.driverObj.destroy();
      },

      onHighlightStarted: async (
        _el: unknown,
        _step: unknown,
        opts: { state?: { activeIndex?: number } },
      ) => {
        const idx = opts?.state?.activeIndex ?? 0;
        const goingForward = idx > this.previousIndex;

        if (this.actionAbortController) {
          this.actionAbortController.abort();
        }
        this.actionAbortController = new AbortController();
        const signal = this.actionAbortController.signal;

        if (this.previousIndex >= 0 && this.previousIndex < steps.length) {
          const leavingStep = steps[this.previousIndex];
          const leavingGroup = leavingStep.modalGroup ?? null;
          const enteringGroup = steps[idx].modalGroup ?? null;

          if (leavingGroup && leavingGroup !== enteringGroup) {
            await this.closeMatDialog();
            if (signal.aborted) return;
            this.cleanAdditionalHighlights();
          }

          if (leavingStep.openSelect) {
            this.closeOpenSelect();
          }
          if (leavingStep.openAccordeon && !goingForward) {
            this.collapseAccordeon(leavingStep);
          }
        }

        this.cleanAdditionalHighlights();
        this.previousIndex = idx;

        const enteringStep = steps[idx];

        if (!goingForward && enteringStep.modalGroup) {
          await this.openModalForGroup(
            steps,
            enteringStep.modalGroup,
            enteringStep.element,
            signal,
          );
          if (signal.aborted) return;
        }

        await this.executeStepActions(enteringStep, goingForward, signal);
      },

      onPopoverRender: (popover: any, options: any) => {
        setTimeout(() => {
          const overlay = document.querySelector(
            '.driver-overlay.driver-overlay-animated',
          ) as HTMLElement;
          if (overlay) overlay.style.zIndex = '10';
        }, 100);

        const currentIndex = options?.state?.activeIndex ?? 0;
        const isFirstStep = currentIndex === 0;
        const isLastStep = currentIndex === processedSteps.length - 1;

        setTimeout(() => {
          const nextOrDoneButton = popover.footerButtons.querySelector('.driver-popover-next-btn');
          const prevButton = popover.footerButtons.querySelector('.driver-popover-prev-btn');
          if (nextOrDoneButton) {
            nextOrDoneButton.setAttribute(
              'data-fs-element',
              isLastStep ? 'Onboarding-Button-Done' : 'Tour-Button-Next',
            );
          }
          if (prevButton) {
            prevButton.setAttribute('data-fs-element', 'Onboarding-Button-Previous');
          }
        }, 0);

        if (!isLastStep) {
          const skipButton = document.createElement('a');
          skipButton.type = 'button';
          skipButton.id = 'skip-onboarding-btn';
          skipButton.innerText = this.translateItem('ONBOARDING.BUTTONS.SKIP_TOUR');
          skipButton.className = 'custom-skip-onboarding-btn';
          skipButton.setAttribute('data-fs-element', 'Tour-Button-Skip');
          const footer = popover.footerButtons;
          footer.firstChild
            ? footer.insertBefore(skipButton, footer.firstChild)
            : footer.appendChild(skipButton);
          skipButton.addEventListener('click', () => {
            this.setSkippedOnboarding();
            this.driverObj.destroy();
          });
        }

        if (isFirstStep) {
          const prevButton = popover.footerButtons.querySelector('.driver-popover-prev-btn');
          if (prevButton) prevButton.style.display = 'none';
        }
      },

      onDestroyed: async () => {
        document.body.classList.remove('onboarding-open-search-background-gallery');
        if (this.actionAbortController) {
          this.actionAbortController.abort();
          this.actionAbortController = null;
        }
        this.previousIndex = -1;
        this.removeInteractionBlocker();
        this.cleanAdditionalHighlights();
        await this.closeMatDialog(200);

        steps.forEach(step => {
          if (step.customSize) this.restoreOriginalSize(step.element);
          if (step.openAccordeon) this.collapseAccordeon(step);
          if (step.openSelect) this.closeOpenSelect();
        });

        if (this.currentKey === key) this.currentKey = null;
        this.tourCompletedVersion.update(version => version + 1);
        this.isTourActive.set(false);
      },
    });

    this.driverObj.setSteps(processedSteps);
    this.driverObj.drive();
  }

  private addInteractionBlocker(): void {
    const blocker = document.createElement('div');
    blocker.classList.add('driver-blocker-overlay');
    blocker.id = 'driver-blocker-overlay';
    document.body.appendChild(blocker);
  }

  private removeInteractionBlocker(): void {
    const blocker = document.getElementById('driver-blocker-overlay');
    if (blocker) document.body.removeChild(blocker);
  }

  private convertToCustomSteps(steps: DriveStep[]): CustomDriveStep[] {
    return steps.map(step => ({
      element: typeof step.element === 'string' ? step.element : '#unknown',
      popover: {
        title: this.translateItem(step.popover?.title),
        description: this.translateItem(step.popover?.description),
        side: (step.popover as any)?.side || 'bottom',
        align: (step.popover as any)?.align || 'start',
      },
    }));
  }

  startTour(steps: CustomDriveStep[] | DriveStep[], key: string, isFooter?: boolean): boolean {
    if (!isFooter && this.hasCompleted(key)) return false;
    if (!(window as any).__karma__ === false) return false;
    if (!isFooter && this.hasSkippedOnboarding()) return false;

    const customSteps =
      Array.isArray(steps) &&
      steps.length > 0 &&
      'element' in steps[0] &&
      typeof steps[0].element !== 'string'
        ? this.convertToCustomSteps(steps as DriveStep[])
        : (steps as CustomDriveStep[]);

    if (this.spinnerService.elementsReady()) {
      this.startTourInternal(customSteps, key);
    } else {
      this.pendingTour = { steps: customSteps, key: key || '' };
    }
    return true;
  }

  hasCompleted(key: string): boolean {
    return (
      this.completedTours.has(key) || localStorage.getItem(this.storageKeyPrefix + key) === 'true'
    );
  }

  getCompletedAt(key: string): number | null {
    const value = localStorage.getItem(`${this.storageKeyPrefix}${key}_completedAt`);
    if (!value) return null;
    const timestamp = Number(value);
    return Number.isFinite(timestamp) ? timestamp : null;
  }

  getCompletionTimestampForRelease(key: string, deploymentTimestamp: number): number | null {
    const completedAt = this.getCompletedAt(key);
    if (completedAt !== null) return completedAt;

    // Older app versions stored only the completion flag. Since this version is
    // deployed with the release date, a legacy flag represents prior-release state.
    return this.hasCompleted(key) ? deploymentTimestamp - 1 : null;
  }

  hasSkippedOnboarding(): boolean {
    return Boolean(
      localStorage.getItem('isSkippedOnboarding') ||
      localStorage.getItem('hiddenTemporalOnboarding'),
    );
  }

  private setCompleted(key: string): void {
    this.completedTours.add(key);
    localStorage.setItem(this.storageKeyPrefix + key, 'true');
    localStorage.setItem(`${this.storageKeyPrefix}${key}_completedAt`, String(Date.now()));
  }

  private setSkippedOnboarding(): void {
    localStorage.setItem('isSkippedOnboarding', 'true');
    localStorage.setItem('isSkippedOnboardingAt', String(Date.now()));
  }

  hasSkippedOnboardingBefore(deploymentTimestamp: number): boolean {
    if (!this.hasSkippedOnboarding()) return false;
    const storedTimestamp = localStorage.getItem('isSkippedOnboardingAt');
    if (storedTimestamp === null) return true;
    const skippedAt = Number(storedTimestamp);
    return Number.isFinite(skippedAt) && skippedAt < deploymentTimestamp;
  }

  resetTour(key: string): void {
    this.completedTours.delete(key);
    localStorage.removeItem(this.storageKeyPrefix + key);
    localStorage.removeItem(`${this.storageKeyPrefix}${key}_completedAt`);
  }

  highlight(
    element: string,
    options?: {
      title?: string;
      description?: string;
      side?: 'left' | 'right' | 'top' | 'bottom' | 'over';
      customSize?: { width?: number; height?: number };
    },
  ): void {
    const found = document.querySelector(element);
    if (!found) {
      console.warn(`[OnBoardingService] Element ${element} not found for highlight.`);
      return;
    }
    if (options?.customSize) this.applyCustomSize(element, options.customSize);
    this.driverObj.highlight({
      element,
      popover: {
        title: this.translateItem(options?.title),
        description: this.translateItem(options?.description),
        side: (options?.side || 'bottom') as any,
        showButtons: ['close'],
        doneBtnText: this.translateItem('ONBOARDING.BUTTONS.DONE'),
        onCloseClick: () => {
          if (options?.customSize) this.restoreOriginalSize(element);
          this.driverObj.destroy();
        },
      },
    });
  }

  destroy(): void {
    if (this.driverObj && typeof this.driverObj.destroy === 'function') {
      this.driverObj.destroy();
    }
  }
}
